import React, { useEffect, useState, useMemo } from 'react';
import Chart from 'react-apexcharts';
import { DateTime } from 'luxon';
import { Typography, Card, Box } from '@mui/material';
import { useSelector } from 'react-redux';
import { http } from '../../../../clients/http/http.client';
import { HTTP_ENDPOINTS } from '../../../../clients/clients.constants';
import Loader from '../../../Inputs/Loader/Loader';

const MonthlyPeakUsageChart = ({ timeZone }) => {
  // Get siteId, fixtures, and selectedDate from Redux
  const siteId = useSelector((state) => state.siteId.siteId);
  const fixtures = useSelector((state) => state.analyticsSelections.equipmentTypeIds);
  const selectedDate = useSelector((state) => state.peakUsageSelectedDate.selectedDate);

  const [monthlyPeakUsages, setMonthlyPeakUsages] = useState([]);
  const [loading, setLoading] = useState(false);

  // Build query params for the endpoint
  const buildQueryParams = ({ siteId, fixtures, date }) => {
    const params = new URLSearchParams();
    if (siteId) params.append('siteId', siteId);
    if (fixtures?.length) params.append('equipmentTypeIds', fixtures.join(','));
    if (date) params.append('date', date);
    return params.toString() ? `?${params.toString()}` : '';
  };

  // Fetch data when siteId, fixtures, or selectedDate changes
  useEffect(() => {
    if (!siteId || !selectedDate) return;
    const fetchData = async () => {
      setLoading(true);
      try {
        const qs = buildQueryParams({ siteId, fixtures, date: selectedDate });
        const res = await http.client.get(`${HTTP_ENDPOINTS.EQUIPMENT_MONTHLY_PEAK_USAGE}${qs}`);
        setMonthlyPeakUsages(res);
      } catch (e) {
        setMonthlyPeakUsages([]);
        // Optionally handle error
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, [siteId, fixtures, selectedDate]);

  // Extract day labels as MM/dd for x-axis categories
  const dayLabels = useMemo(() => monthlyPeakUsages.map(day =>
    DateTime.fromISO(day.date).toFormat('MM/dd')
  ), [monthlyPeakUsages]);

  // Extract peakInUse values for the bar chart series
  const peakInUseData = useMemo(() => monthlyPeakUsages.map(day => day.peakInUse), [monthlyPeakUsages]);

  // Chart options, similar to weekly chart but adapted for monthly view
  const options = {
    chart: {
      type: 'bar',
      height: 200,
      toolbar: {
        show: false,
      },
    },
    plotOptions: {
      bar: {
        horizontal: false,
        columnWidth: '55%',
        borderRadius: 3,
      },
    },
    dataLabels: {
      enabled: false,
    },
    stroke: {
      show: true,
      width: 2,
      colors: ['transparent'],
    },
    xaxis: {
      categories: dayLabels,
      labels: {
        style: { fontSize: '12px', color: '#4A5568' },
        show: true,
        showDuplicates: false,
        rotateAlways: true,
        hideOverlappingLabels: false,
        maxHeight: 120,
        rotate: -45,
      },
      title: {
        style: {
          fontSize: '10px',
          color: '#4A5568',
        },
      },
    },
    yaxis: {
      title: {
        style: {
          fontSize: '10px',
          color: '#4A5568',
        },
      },
      labels: {
        style: { fontSize: '12px', color: '#4A5568' },
      },
    },
    fill: {
      opacity: 1,
    },
    colors: ['#89D8C2'],
    tooltip: {
      custom: function ({ series, seriesIndex, dataPointIndex }) {
        const dayData = monthlyPeakUsages[dataPointIndex];
        const dayNumber = dayLabels[dataPointIndex];
        const peakInUse = dayData?.peakInUse;
        
        // Format peak periods in the specified time zone
        const peakPeriods = (dayData?.peakPeriods || []).map(period => {
          const start = DateTime.fromISO(period.peakStart, { zone: 'utc' })
            .setZone(timeZone)
            .toFormat('hh:mm a');
          const end = DateTime.fromISO(period.peakEnd, { zone: 'utc' })
            .setZone(timeZone)
            .toFormat('hh:mm a');
          return `${start} - ${end}`;
        });

        // Create HTML for peak periods
        const peakPeriodsHtml = peakPeriods.length > 0
          ? peakPeriods.map(period => `<div>${period}</div>`).join('')
          : '<div>None</div>';

        // Format the date
        const formattedDate = dayData?.date
          ? DateTime.fromISO(dayData.date).toFormat('MM/dd/yyyy')
          : 'N/A';

        return `
          <div style="padding: 10px; background: #2D3748; color: #fff; border-radius: 8px; font-size: 12px;">
            <strong>Day ${dayNumber} - ${formattedDate}</strong><br/>
            Peak Usage: ${peakInUse}<br/>
            Peak Times:<br/>
            ${peakPeriodsHtml}
          </div>
        `;
      },
    },
    legend: {
      show: false,
    },
    grid: {
      show: true,
      strokeDashArray: 0,
      position: 'back',
      xaxis: {
        lines: {
          show: false
        }
      },
      yaxis: {
        lines: {
          show: true
        }
      },
    },
  };

  // Series data: single series for peakInUse
  const series = [
    {
      name: 'Peak In Use',
      data: peakInUseData,
    },
  ];

  // Get month name for display
  const monthName = monthlyPeakUsages.length > 0 
    ? DateTime.fromISO(monthlyPeakUsages[0].date).toFormat('MMMM yyyy')
    : 'Monthly View';

  return (
    <Card
      sx={{
        p: 2,
        borderRadius: '12px',
        boxShadow: '0px 4px 40px 0px rgba(226, 236, 249, 0.60)',
        backgroundColor: '#fff',
        flex: 1,
        display: 'flex',
        flexDirection: 'column',
      }}
    >
      <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 2, minHeight: "auto" }}>
        <Typography
          sx={{
            fontSize: '22px',
            lineHeight: '22px',
            fontWeight: 600,
            color: '#1B3665',
          }}
        >
          Monthly Peak Usage
        </Typography>
        <Typography
          sx={{
            fontSize: '16px',
            fontWeight: 600,
            color: '#1B3665',
            alignSelf: 'center',
          }}
        >
          {monthName}
        </Typography>
      </Box>
      <Box sx={{ flex: 1, maxHeight: "400px", overflow: "hidden" }}>
        {loading ? (
          <Loader sx={{ height: '400px' }} />
        ) : (
          <Chart
            options={options}
            series={series}
            type="bar"
            height={400}
          />
        )}
      </Box>
    </Card>
  );
};

export default MonthlyPeakUsageChart; 